// Animation variants for different use cases
export const fadeInVariants = {
    initial: { opacity: 0.01, y: 10 },
    animate: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.25,
            ease: [0.22, 1, 0.36, 1],
        },
    },
}

export const slideVariants = {
    initial: {
        opacity: 0.01,
        x: 12,
        scale: 0.995,
    },
    in: {
        opacity: 1,
        x: 0,
        scale: 1,
    },
    out: {
        opacity: 0.01,
        x: -8,
        scale: 1.005,
    },
}

export const gentleSlideVariants = {
    initial: {
        opacity: 0.01,
        x: 6,
        scale: 0.998,
    },
    in: {
        opacity: 1,
        x: 0,
        scale: 1,
    },
    out: {
        opacity: 0.01,
        x: -4,
        scale: 1.002,
    },
}

export const scaleVariants = {
    initial: { opacity: 0.01, scale: 0.95 },
    animate: {
        opacity: 1,
        scale: 1,
        transition: {
            duration: 0.3,
            ease: [0.22, 1, 0.36, 1],
        },
    },
}

export const staggerContainer = {
    hidden: { opacity: 0.01 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1,
            delayChildren: 0.2,
        },
    },
}

export const staggerItem = {
    hidden: { opacity: 0.01, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            type: 'spring',
            stiffness: 100,
            damping: 10,
        },
    },
}

// Common transitions
export const smoothTransition = {
    type: 'tween',
    ease: [0.16, 1, 0.3, 1],
    duration: 0.45,
}

export const springTransition = {
    type: 'spring',
    stiffness: 300,
    damping: 20,
}

export const fastTransition = {
    type: 'tween',
    ease: [0.22, 1, 0.36, 1],
    duration: 0.2,
}

export const gentleTransition = {
    type: 'tween',
    ease: [0.12, 0, 0.39, 0],
    duration: 0.6,
}

// Hover animations
export const hoverScale = {
    scale: 1.02,
    transition: springTransition,
}

export const hoverScaleSmall = {
    scale: 1.01,
    transition: springTransition,
}

export const hoverBackground = {
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
    transition: { duration: 0.2 },
}

// Bounce animations
export const bounceVariants = {
    animate: {
        y: [0, -10, 0],
        transition: {
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut',
        },
    },
}

export const bounceChevronVariants = {
    animate: {
        y: [0, -5, 0],
        transition: {
            duration: 1.5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 0.2,
        },
    },
}

export const bounceHover = {
    scale: 1.05,
    transition: springTransition,
}

export const transitionBase = {
    type: 'tween' as const,
    ease: [0.22, 1, 0.36, 1],
    duration: 0.7,
}

// Alternative elegant curves for variety
export const smoothEaseOut = {
    type: 'tween' as const,
    ease: [0.16, 1, 0.3, 1],
    duration: 0.5,
}

export const gentleEaseOut = {
    type: 'tween' as const,
    ease: [0.19, 1, 0.22, 1],
    duration: 0.5,
}

export const headerFoucVariants = {
    hidden: { opacity: 0.01 },
    visible: {
        opacity: 1,
        transition: {
            duration: 0.35,
            delay: 0.06,
            ease: [0.22, 1, 0.36, 1],
        },
    },
}

// Text fade-in variants (common pattern: opacity + y)
export const textFadeInVariants = {
    hidden: { opacity: 0.01, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: transitionBase,
    },
}

// Text fade-in with exit (for carousels/sliders)
export const textFadeInOutVariants = {
    hidden: { opacity: 0.01, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: transitionBase,
    },
    exit: {
        opacity: 0.01,
        y: -20,
        transition: {
            ...transitionBase,
            duration: 0.5,
        },
    },
}

export const staggerConfig = {
    staggerChildren: 0.12,
    delayChildren: 0.15,
}

export const textFadeInVariantsNoTransition = {
    hidden: { opacity: 0.01, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.65,
            ease: [0.22, 1, 0.36, 1],
        },
    },
}

export const imageScaleVariants = {
    hidden: { opacity: 0.01, scale: 1.05 },
    visible: {
        opacity: 1,
        scale: 1,
        transition: {
            ...transitionBase,
            duration: 0.9,
        },
    },
}

export const slideInVariants = {
    hidden: { opacity: 0.01, x: 30, y: 10 },
    visible: {
        opacity: 1,
        x: 0,
        y: 0,
        transition: transitionBase,
    },
}

export const slideInVariantsLarge = {
    hidden: { opacity: 0.01, x: 100 },
    visible: {
        opacity: 1,
        x: 0,
        transition: {
            ...transitionBase,
            duration: 0.8,
        },
    },
}

// Background fade variants (with exit for carousels)
export const backgroundFadeVariants = {
    hidden: { opacity: 0.01 },
    visible: {
        opacity: 1,
        transition: {
            ...transitionBase,
            duration: 1.0,
        },
    },
    exit: {
        opacity: 0.01,
        transition: {
            ...transitionBase,
            duration: 0.7,
        },
    },
}

// Word animation variants (for HomeHero split text) - 3D flip effect
export const wordAnimationVariants = {
    hidden: {
        opacity: 0.01,
        y: 30,
        rotateX: -70,
        z: -50,
    },
    visible: (custom: number | { i: number }) => {
        const i = typeof custom === 'number' ? custom : custom.i
        return {
            opacity: 1,
            y: 0,
            rotateX: 0,
            z: 0,
            transition: {
                duration: 0.6,
                delay: i * 0.05,
                type: 'tween' as const,
                ease: [0.22, 1, 0.36, 1],
            },
        }
    },
    exit: (custom: number | { i: number; totalWords: number }) => {
        const isObject = typeof custom === 'object'
        const i = isObject ? custom.i : custom
        const totalWords = isObject ? custom.totalWords : 1
        const reverseIndex = totalWords - 1 - i
        return {
            opacity: 0.01,
            y: -30,
            rotateX: 70,
            z: -50,
            transition: {
                duration: 0.4,
                delay: reverseIndex * 0.03,
                type: 'tween' as const,
                ease: [0.22, 1, 0.36, 1],
            },
        }
    },
}

// Row animation variants (for HomeHero)
export const rowAnimationVariants = {
    hidden: { opacity: 0.01, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: transitionBase,
    },
}

// Card fade-in variants (for TabbedCard and similar)
export const cardFadeInVariants = {
    hidden: { opacity: 0.01 },
    visible: {
        opacity: 1,
        transition: transitionBase,
    },
}

// Card content fade-in variants (with delay support)
export const cardContentFadeInVariants = (delay: number = 0) => ({
    hidden: { opacity: 0.01, y: 10 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            ...transitionBase,
            delay,
        },
    },
})

// Button hover variants
export const buttonHoverVariants = {
    scale: 1.05,
    transition: {
        ...transitionBase,
        duration: 0.3,
    },
}

export const buttonTapVariants = {
    scale: 0.98,
    transition: {
        ...transitionBase,
        duration: 0.2,
    },
}

export const buttonHoverVariantsSmall = {
    scale: 1.02,
    transition: {
        ...transitionBase,
        duration: 0.3,
    },
}

// Browse dropdown animation variants
export const browseDropdownContainerVariants = {
    hidden: { opacity: 0.01 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.08,
            delayChildren: 0.05,
        },
    },
}

export const browseDropdownColumnVariants = {
    hidden: { opacity: 0.01, y: 10 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.3,
            ease: 'easeOut',
            staggerChildren: 0.03,
            delayChildren: 0.1,
        },
    },
}

export const browseDropdownItemVariants = {
    hidden: { opacity: 0.01, x: -8 },
    visible: {
        opacity: 1,
        x: 0,
        transition: {
            duration: 0.25,
            ease: 'easeOut',
        },
    },
}
